import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import MilestoneModal from '../components/MilestoneModal';
import UpdateModal from '../components/UpdateModal';
import KPIModal from '../components/KPIModal';
import ProjectCommunication from '../components/ProjectCommunication';
import { 
  ArrowLeft, CheckCircle, Clock, AlertCircle, DollarSign, 
  Calendar, Upload, FileText, MessageSquare, TrendingUp,
  Download, Users, Target, BarChart3, Award, Plus
} from 'lucide-react';

const EngagementDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [kpis, setKPIs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Modal states
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showKPIModal, setShowKPIModal] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  useEffect(() => {
    checkAuth();
  }, [id]);

  const checkAuth = async () => {
    const token = localStorage.getItem('xchange_token');
    const userData = localStorage.getItem('xchange_user');

    if (!token || !userData) {
      navigate('/xchange/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    await fetchEngagementData(token);
  };

  const fetchEngagementData = async (token) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

      // Fetch engagement
      const engRes = await fetch(`${backendUrl}/api/saas/engagements/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const engData = await engRes.json();
      setEngagement(engData);

      // Fetch milestones
      const milestoneRes = await fetch(`${backendUrl}/api/saas/milestones?engagementId=${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const milestoneData = await milestoneRes.json();
      setMilestones(milestoneData);

      // Fetch updates
      const updateRes = await fetch(`${backendUrl}/api/saas/updates?engagementId=${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const updateData = await updateRes.json();
      setUpdates(updateData);

      // Fetch KPIs
      const kpiRes = await fetch(`${backendUrl}/api/saas/kpis?engagementId=${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const kpiData = await kpiRes.json();
      setKPIs(kpiData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching engagement data:', error);
      setLoading(false);
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'on_track': return '#10B981';
      case 'at_risk': return '#F59E0B';
      case 'off_track': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': '#9CA3AF',
      'in_progress': '#3B82F6',
      'submitted': '#F59E0B',
      'approved': '#10B981',
      'completed': '#059669',
      'rejected': '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  const calculateProgress = () => {
    if (milestones.length === 0) return 0;
    const completed = milestones.filter(m => m.status === 'completed').length;
    return Math.round((completed / milestones.length) * 100);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount * 100000);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCreateMilestone = async (milestoneData) => {
    try {
      const token = localStorage.getItem('xchange_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/saas/milestones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(milestoneData)
      });

      if (response.ok) {
        alert('Milestone created successfully!');
        setShowMilestoneModal(false);
        await fetchEngagementData(token);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating milestone:', error);
      alert('Failed to create milestone');
    }
  };

  const handleSubmitUpdate = async (updateData) => {
    try {
      const token = localStorage.getItem('xchange_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/saas/updates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        alert('Update submitted successfully!');
        setShowUpdateModal(false);
        await fetchEngagementData(token);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error submitting update:', error);
      alert('Failed to submit update');
    }
  };

  const handleCreateKPI = async (kpiData) => {
    try {
      const token = localStorage.getItem('xchange_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/saas/kpis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(kpiData)
      });

      if (response.ok) {
        alert('KPI added successfully!');
        setShowKPIModal(false);
        await fetchEngagementData(token);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating KPI:', error);
      alert('Failed to add KPI');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{borderColor: '#14B8A6'}}></div>
          <p className="mt-4 text-gray-600">Loading engagement details...</p>
        </div>
      </div>
    );
  }

  if (!engagement) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-24 px-4">
          <div className="max-w-7xl mx-auto text-center py-12">
            <p className="text-gray-600">Engagement not found</p>
          </div>
        </div>
      </div>
    );
  }

  const progress = calculateProgress();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>

          {/* Header Section */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold" style={{color: '#1E3A5F'}}>
                    {engagement.projectTitle}
                  </h1>
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${getHealthColor(engagement.healthStatus)}15`,
                      color: getHealthColor(engagement.healthStatus)
                    }}
                  >
                    {engagement.healthStatus.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <p className="text-gray-600">{engagement.description}</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Sponsor</span>
                </div>
                <div className="font-semibold" style={{color: '#1E3A5F'}}>
                  {engagement.companyName}
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Target className="w-4 h-4" />
                  <span className="text-sm">Implementing Agency</span>
                </div>
                <div className="font-semibold" style={{color: '#1E3A5F'}}>
                  {engagement.agencyName}
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">Total Budget</span>
                </div>
                <div className="font-semibold" style={{color: '#1E3A5F'}}>
                  {formatCurrency(engagement.totalBudget)}
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Duration</span>
                </div>
                <div className="font-semibold" style={{color: '#1E3A5F'}}>
                  {formatDate(engagement.startDate)} - {formatDate(engagement.endDate)}
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                <span className="text-sm font-bold" style={{color: '#14B8A6'}}>{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="h-3 rounded-full transition-all"
                  style={{
                    width: `${progress}%`,
                    background: 'linear-gradient(90deg, #14B8A6 0%, #10B981 100%)'
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b border-gray-200">
            <div className="flex gap-6 overflow-x-auto">
              {['overview', 'milestones', 'updates', 'kpis', 'financials', 'documents', 'communication'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-3 px-1 font-medium transition-all capitalize whitespace-nowrap ${
                    activeTab === tab
                      ? 'border-b-2 text-teal-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  style={activeTab === tab ? {borderColor: '#14B8A6'} : {}}
                >
                  {tab === 'kpis' ? 'KPIs' : tab}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Milestones Summary */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold mb-4" style={{color: '#1E3A5F'}}>
                    Milestones Summary
                  </h3>
                  <div className="space-y-3">
                    {milestones.slice(0, 3).map((milestone) => (
                      <div key={milestone.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium text-sm" style={{color: '#1E3A5F'}}>
                            {milestone.title}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Due: {formatDate(milestone.dueDate)}
                          </div>
                        </div>
                        <span 
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{
                            backgroundColor: `${getStatusColor(milestone.status)}15`,
                            color: getStatusColor(milestone.status)
                          }}
                        >
                          {milestone.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Updates */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold mb-4" style={{color: '#1E3A5F'}}>
                    Recent Updates
                  </h3>
                  <div className="space-y-3">
                    {updates.slice(0, 3).map((update) => (
                      <div key={update.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500">
                            {formatDate(update.createdAt)}
                          </span>
                          <span 
                            className="px-2 py-1 rounded text-xs font-medium"
                            style={{
                              backgroundColor: `${getStatusColor(update.status)}15`,
                              color: getStatusColor(update.status)
                            }}
                          >
                            {update.status}
                          </span>
                        </div>
                        <div className="text-sm text-gray-700">{update.description}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* KPIs */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold mb-4" style={{color: '#1E3A5F'}}>
                    Key Performance Indicators
                  </h3>
                  <div className="space-y-3">
                    {kpis.slice(0, 3).map((kpi) => (
                      <div key={kpi.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium" style={{color: '#1E3A5F'}}>
                            {kpi.name}
                          </span>
                          <span className="text-sm font-bold" style={{color: '#14B8A6'}}>
                            {kpi.actualValue} / {kpi.targetValue}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{kpi.unit}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold mb-4" style={{color: '#1E3A5F'}}>
                    Quick Actions
                  </h3>
                  <div className="space-y-2">
                    {user?.role === 'ngo' && (
                      <>
                        <button
                          onClick={() => setShowUpdateModal(true)}
                          className="w-full px-4 py-3 rounded-lg font-medium transition-all hover:shadow-md flex items-center gap-2 justify-center"
                          style={{background: '#14B8A6', color: '#FFFFFF'}}
                        >
                          <Upload className="w-4 h-4" />
                          Submit Progress Update
                        </button>
                        <button
                          className="w-full px-4 py-3 rounded-lg font-medium transition-all hover:shadow-md flex items-center gap-2 justify-center border-2"
                          style={{borderColor: '#14B8A6', color: '#14B8A6'}}
                        >
                          <FileText className="w-4 h-4" />
                          Upload Documents
                        </button>
                      </>
                    )}
                    {user?.role === 'corporate' && (
                      <button
                        className="w-full px-4 py-3 rounded-lg font-medium transition-all hover:shadow-md flex items-center gap-2 justify-center"
                        style={{background: '#1E3A5F', color: '#FFFFFF'}}
                      >
                        <Download className="w-4 h-4" />
                        Generate Report
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'milestones' && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold" style={{color: '#1E3A5F'}}>
                    Project Milestones
                  </h3>
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => setShowMilestoneModal(true)}
                      className="px-4 py-2 rounded-lg font-medium transition-all"
                      style={{background: '#14B8A6', color: '#FFFFFF'}}
                    >
                      Add Milestone
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {milestones.map((milestone, index) => (
                    <div key={milestone.id} className="p-5 border-2 rounded-xl hover:border-teal-200 transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-4 flex-1">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold"
                            style={{background: '#14B8A6', color: '#FFFFFF'}}
                          >
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg mb-1" style={{color: '#1E3A5F'}}>
                              {milestone.title}
                            </h4>
                            <p className="text-gray-600 text-sm mb-2">{milestone.description}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>Due: {formatDate(milestone.dueDate)}</span>
                              <span>•</span>
                              <span>Progress: {milestone.progressPercentage}%</span>
                            </div>
                          </div>
                        </div>
                        <span 
                          className="px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${getStatusColor(milestone.status)}15`,
                            color: getStatusColor(milestone.status)
                          }}
                        >
                          {milestone.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      
                      {milestone.deliverables && milestone.deliverables.length > 0 && (
                        <div className="mt-3 pl-14">
                          <div className="text-sm font-medium text-gray-700 mb-2">Deliverables:</div>
                          <ul className="space-y-1">
                            {milestone.deliverables.map((del, idx) => (
                              <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                {del}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'updates' && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold mb-6" style={{color: '#1E3A5F'}}>
                  Progress Updates
                </h3>
                
                <div className="space-y-4">
                  {updates.map((update) => (
                    <div key={update.id} className="p-5 border border-gray-200 rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="text-sm text-gray-500 mb-1">
                            {formatDate(update.createdAt)} • {update.submittedBy}
                          </div>
                          <h4 className="font-semibold" style={{color: '#1E3A5F'}}>
                            {update.title}
                          </h4>
                        </div>
                        <span 
                          className="px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${getStatusColor(update.status)}15`,
                            color: getStatusColor(update.status)
                          }}
                        >
                          {update.status}
                        </span>
                      </div>
                      <p className="text-gray-700 mb-3">{update.description}</p>
                      
                      {update.evidenceUrls && update.evidenceUrls.length > 0 && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FileText className="w-4 h-4" />
                          <span>{update.evidenceUrls.length} document(s) attached</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'kpis' && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold" style={{color: '#1E3A5F'}}>
                    Key Performance Indicators
                  </h3>
                  {(user?.role === 'admin' || user?.role === 'ngo') && (
                    <button
                      onClick={() => setShowKPIModal(true)}
                      className="px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2"
                      style={{background: '#14B8A6', color: '#FFFFFF'}}
                    >
                      <Plus className="w-4 h-4" />
                      Add KPI
                    </button>
                  )}
                </div>

                {kpis.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>No KPIs defined yet</p>
                    <p className="text-sm mt-2">Add key performance indicators to track project impact</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {kpis.map((kpi) => {
                      const progress = kpi.targetValue > 0 ? (kpi.actualValue / kpi.targetValue) * 100 : 0;
                      const progressColor = progress >= 80 ? '#10B981' : progress >= 50 ? '#F59E0B' : '#EF4444';
                      
                      return (
                        <div key={kpi.id} className="p-5 border-2 border-gray-200 rounded-xl hover:border-teal-200 transition-all">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span 
                                  className="px-2 py-1 rounded text-xs font-medium"
                                  style={{
                                    background: 'rgba(20, 184, 166, 0.1)',
                                    color: '#14B8A6'
                                  }}
                                >
                                  {kpi.category}
                                </span>
                              </div>
                              <h4 className="font-semibold text-lg mb-2" style={{color: '#1E3A5F'}}>
                                {kpi.name}
                              </h4>
                              {kpi.description && (
                                <p className="text-sm text-gray-600 mb-3">{kpi.description}</p>
                              )}
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Progress</span>
                              <span className="font-bold" style={{color: progressColor}}>
                                {progress.toFixed(1)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full transition-all"
                                style={{
                                  width: `${Math.min(progress, 100)}%`,
                                  backgroundColor: progressColor
                                }}
                              ></div>
                            </div>
                            <div className="flex items-center justify-between pt-2">
                              <div>
                                <div className="text-xs text-gray-500">Actual</div>
                                <div className="text-xl font-bold" style={{color: '#1E3A5F'}}>
                                  {kpi.actualValue}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-gray-500">Target</div>
                                <div className="text-xl font-bold text-gray-600">
                                  {kpi.targetValue}
                                </div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 text-center pt-2 border-t">
                              Unit: {kpi.unit}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'financials' && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold mb-6" style={{color: '#1E3A5F'}}>
                  Financial Monitoring
                </h3>
                
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div className="p-5 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-sm text-green-700 mb-2">Total Budget</div>
                    <div className="text-2xl font-bold text-green-900">
                      {formatCurrency(engagement.totalBudget)}
                    </div>
                  </div>
                  <div className="p-5 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-sm text-blue-700 mb-2">Disbursed</div>
                    <div className="text-2xl font-bold text-blue-900">
                      {formatCurrency(engagement.totalBudget * 0.6)}
                    </div>
                  </div>
                  <div className="p-5 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="text-sm text-amber-700 mb-2">Utilized</div>
                    <div className="text-2xl font-bold text-amber-900">
                      {formatCurrency(engagement.totalBudget * 0.45)}
                    </div>
                  </div>
                </div>

                <div className="text-center py-8 text-gray-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>Detailed disbursement schedule coming soon</p>
                </div>
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold mb-6" style={{color: '#1E3A5F'}}>
                  Document Repository
                </h3>
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>Document management interface coming soon</p>
                  <p className="text-sm mt-2">Upload and organize project documents, invoices, and evidence</p>
                </div>
              </div>
            )}

            {activeTab === 'communication' && (
              <div>
                <ProjectCommunication 
                  engagementId={id}
                  engagement={engagement}
                  currentUser={user}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <MilestoneModal
        show={showMilestoneModal}
        onClose={() => setShowMilestoneModal(false)}
        onSubmit={handleCreateMilestone}
        engagementId={id}
      />

      <UpdateModal
        show={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onSubmit={handleSubmitUpdate}
        engagementId={id}
        milestones={milestones}
      />

      <KPIModal
        show={showKPIModal}
        onClose={() => setShowKPIModal(false)}
        onSubmit={handleCreateKPI}
        engagementId={id}
      />
    </div>
  );
};

export default EngagementDetailPage;
